import React from 'react';
import ReactDOM from 'react-dom';
import Icon from '@/components/ui/Icon';
import FroalaDeletePopover from '../shared/FroalaDeletePopover';

interface TextVariableDeleteOptions {
  className: string; // CSS class to identify the text variable element
  popoverRootId: string; // Unique ID for the popover root element
  deleteIcon?: React.ReactNode;
  popoverConfig?: {
    width?: string;
    height?: string;
    offset?: { top?: number; left?: number };
    position?: 'bottom-left' | 'top-right';
    showCaret?: boolean;
  };
}

// This behavior was modeled off that of the `Froala/plugins/MainIdeas` component
export const handleTextVariableDeleteClick = (
  e: MouseEvent,
  selector: string,
  editorInstance: any,
  options: TextVariableDeleteOptions,
) => {
  const target = e.target as HTMLElement;

  // Check if the clicked element is the target text variable
  if (!target.classList.contains(options.className)
      && !target.closest(`.${options.className}`)) {
    return;
  }

  const textVariableElement = target.classList.contains(options.className)
    ? target
    : target.closest(`.${options.className}`) as HTMLElement;

  if (!textVariableElement) return;

  const editor = document.querySelector(`${selector} .fr-view`) as HTMLElement;
  if (!editor) return;

  const popoverRoot = document.querySelector(`#${options.popoverRootId}`) || document.createElement('div');
  popoverRoot.id = options.popoverRootId;

  e.stopPropagation();
  e.preventDefault();

  const handleDelete = () => {
    if (!textVariableElement.parentNode) return;

    // Remove the text variable element and any following <br/> if it exists
    const { nextSibling } = textVariableElement;
    if (nextSibling && nextSibling.nodeName === 'BR') {
      nextSibling.remove();
    }
    textVariableElement.remove();

    ReactDOM.unmountComponentAtNode(popoverRoot);
    popoverRoot.remove();
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (!popoverRoot.contains(event.target as Node)
        && !textVariableElement.contains(event.target as Node)) {
      ReactDOM.unmountComponentAtNode(popoverRoot);
      popoverRoot.remove();
      document.removeEventListener('mousedown', handleClickOutside);
    }
  };

  const defaultConfig = {
    height: 'tw-h-8',
    offset: { left: 8, top: 0 },
    position: 'top-right' as const,
    showCaret: false,
    width: 'tw-w-8',
  };

  const config = { ...defaultConfig, ...options.popoverConfig };
  const deleteIcon = options.deleteIcon || <Icon fill="#D2232A" icon="Delete" />;

  ReactDOM.render(
    <FroalaDeletePopover
      deleteIcon={deleteIcon}
      editorInstance={editorInstance}
      editorTop={editor.getBoundingClientRect().top}
      height={config.height}
      offset={config.offset}
      position={config.position}
      showCaret={config.showCaret}
      targetElement={textVariableElement}
      width={config.width}
      onDelete={handleDelete}
    />,
    popoverRoot,
  );

  if (!document.body.contains(popoverRoot)) {
    document.body.appendChild(popoverRoot);
  }

  requestAnimationFrame(() => {
    document.addEventListener('mousedown', handleClickOutside);
  });
};
